import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { getContentForDocumentPreview } from "../actions/documentActions";
import { getDocumentPreviewType } from "../utilities/utilities";

const documentPreviewTypes = {
	PDF_INLINE: "PDF",
	DOCX_INLINE: "Docx",
	EXCEL_INLINE: "Excel",
	IMAGE_INLINE: "Image",
};

/*
	Shared state + content fetching for the document/email/note preview
	sliding pane (DocumentSlidingPane).

	openItemPreview accepts:
		- type: "File" | "Email" | "Note" | "Matter"
		- id: documentID / emailID / matterNoteID / matterID
		- title: document filename / email subject / note name / matter name
		- rowData: optional extra fields exposed to the pane via
		  selectedRowData (e.g. isFavorite, versionNumber, emailFrom,
		  emailTo, date)

	openItemAI opens the pane in document AI conversation mode for the
	given row (no preview content fetch needed).
*/
const useDocumentPreviewPane = () => {
	const dispatch = useDispatch();
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewSelectedRow, setPreviewSelectedRow] = useState(null);
	const [aiMode, setAiMode] = useState(false);

	const openItemPreview = ({ type, id, title, rowData = {} }) => {
		setAiMode(false);
		if (type === "File") {
			setPreviewSelectedRow({
				type,
				id,
				filename: title,
				isFavorite: false,
				...rowData,
			});
			dispatch(
				getContentForDocumentPreview({
					documentID: id,
					versionNumber: rowData.versionNumber,
					versionID: rowData.versionID,
					previewType:
						documentPreviewTypes[getDocumentPreviewType(title)] ||
						"Document",
				})
			);
		} else if (type === "Email") {
			setPreviewSelectedRow({
				type,
				id,
				subjectline: title,
				originalFilename: "email.msg",
				...rowData,
			});
			dispatch(
				getContentForDocumentPreview({
					emailID: id,
					previewType: "Email",
				})
			);
		} else if (type === "Note") {
			setPreviewSelectedRow({ type, id, name: title, ...rowData });
			dispatch(
				getContentForDocumentPreview({
					noteID: id,
					previewType: "MatterNote",
				})
			);
		} else if (type === "Matter") {
			setPreviewSelectedRow({ type, id, name: title, ...rowData });
			dispatch(
				getContentForDocumentPreview({
					matterID: id,
					previewType: "Matter",
				})
			);
		}
		setPreviewOpen(true);
	};

	const openItemAI = (rowData) => {
		setPreviewSelectedRow(rowData);
		setAiMode(true);
		setPreviewOpen(true);
	};

	return {
		previewOpen,
		previewSelectedRow,
		aiMode,
		openItemPreview,
		openItemAI,
		closePreview: () => setPreviewOpen(false),
	};
};

/*
	For class components, which cannot call hooks directly: injects the
	hook's return value as a `documentPreviewPane` prop.
*/
const withDocumentPreviewPane = (Component) => {
	const WithDocumentPreviewPane = (props) => {
		const documentPreviewPane = useDocumentPreviewPane();
		return (
			<Component {...props} documentPreviewPane={documentPreviewPane} />
		);
	};
	return WithDocumentPreviewPane;
};

export { withDocumentPreviewPane };
export default useDocumentPreviewPane;
